import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from './Icon.tsx';
import { panelStyle, usePopover } from './usePopover.ts';
import { useT } from '~/i18n/index.tsx';

export interface SelectOption {
  value: string;
  label: string;
}

/** Below this many options the eye is faster than a filter. */
const SEARCH_FROM = 8;

/**
 * The panel's dropdown — a listbox, not a native `<select>`.
 *
 * A native select paints its own popup from the OS: system font, system row height, system
 * highlight, and on Windows a hard blue bar that belongs to no design system. It cannot show a
 * tick against the chosen row, cannot be styled per option, and on a long list gives no way to
 * type toward a value. The field shell was ours and the list was not, so every filter row in the
 * panel changed character the moment it was opened.
 *
 * This is the reference panel's `Select`, ported and typed. It keeps the keyboard contract a
 * listbox owes: up/down to move, Home/End to jump, Enter or Space to take, Escape to leave, and
 * type-ahead that jumps to the first option starting with what you typed.
 *
 * The panel is portalled and anchored in script ({@link usePopover}) because these open inside a
 * filter bar and a table toolbar that both clip.
 *
 * A list long enough to scroll carries a search field of its own — categories are the reason; a
 * shop with sixty of them is not browsable by dragging a scrollbar. It matches ANYWHERE in the
 * label, not just the start, since nobody remembers a category's first word exactly.
 *
 * @param placeholder  the empty row, kept selectable so a filter can be cleared
 * @param searchable  forces the field on or off; by default the list decides for itself
 */
export function Select({
  id,
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  invalid = false,
  searchable,
  className = '',
  ...rest
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options?: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  searchable?: boolean;
  className?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'onChange' | 'id'>) {
  const t = useT();
  const { open, setOpen, close, pos, rootRef, panelRef, id: panelId } = usePopover({
    align: 'start',
    matchWidth: true,
  });

  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const withSearch = searchable ?? options.length >= SEARCH_FROM;

  /*
   * The empty row is an option like any other, so one list drives the keyboard, the rendering and
   * the lookup. It is dropped while a search is up: "clear the choice" is not one of the results
   * of typing a name.
   */
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = needle
      ? options.filter((option) => option.label.toLowerCase().includes(needle))
      : options;

    return placeholder != null && !needle ? [{ value: '', label: placeholder }, ...matches] : matches;
  }, [options, placeholder, query]);

  const selectedIndex = rows.findIndex((row) => row.value === value);
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const listRef = useRef<HTMLUListElement>(null);
  const typed = useRef({ text: '', at: 0 });

  // Opening lands on the current value, wherever the highlight was left before.
  useEffect(() => {
    if (open) {
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [open, selectedIndex]);

  /*
   * A dropdown never opens onto the last search, and the caret starts in the field so the first
   * keystroke is already the search.
   */
  useEffect(() => {
    if (open) {
      searchRef.current?.focus();
    } else {
      setQuery('');
    }
  }, [open]);

  // Typing narrows the list under the highlight, which then has to come back to the top.
  useEffect(() => setActiveIndex(0), [query]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (open) {
      listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [open, activeIndex]);

  function take(index: number) {
    const row = rows[index];

    if (!row) {
      return;
    }

    onChange(row.value);
    close();
    rootRef.current?.querySelector('button')?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (disabled) {
      return;
    }

    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        setOpen(true);
      }

      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, rows.length - 1));

        return;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));

        return;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);

        return;
      case 'End':
        event.preventDefault();
        setActiveIndex(rows.length - 1);

        return;
      case 'Enter':
        event.preventDefault();
        take(activeIndex);

        return;
      case ' ':
        /*
         * Space takes the highlighted row — except while typing in the search field, where a space
         * is a space and a two-word category name has one in the middle.
         */
        if (withSearch) {
          return;
        }

        event.preventDefault();
        take(activeIndex);

        return;
      case 'Tab':
      case 'Escape':
        close();
        rootRef.current?.querySelector('button')?.focus();

        return;
      default:
        break;
    }

    /*
     * Type-ahead: successive letters within a second build one search string. Only where there is
     * no search field — with one, the letters belong in it.
     */
    if (!withSearch && event.key.length === 1) {
      const now = event.timeStamp;

      typed.current.text = now - typed.current.at > 900 ? event.key : typed.current.text + event.key;
      typed.current.at = now;

      const needle = typed.current.text.toLowerCase();
      const found = rows.findIndex((row) => row.label.toLowerCase().startsWith(needle));

      if (found >= 0) {
        setActiveIndex(found);
      }
    }
  }

  const selected = selectedIndex >= 0 ? rows[selectedIndex] : null;
  const showsPlaceholder = !selected || selected.value === '';

  return (
    <div className={`select ${className}`.trim()} ref={rootRef}>
      <button
        id={id}
        type="button"
        className={`select__control${open ? ' is-open' : ''}${showsPlaceholder ? ' is-empty' : ''}${
          invalid ? ' is-invalid' : ''
        }`}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => setOpen((state) => !state)}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        {...rest}
      >
        <span className="select__value">
          <bdi>{selected ? selected.label : (placeholder ?? '')}</bdi>
        </span>
        <Icon name="chevronDown" className="select__caret" size={16} strokeWidth={2} />
      </button>

      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              className={`select__panel select__panel--${pos?.placement ?? 'below'}`}
              style={panelStyle(pos)}
            >
              {withSearch ? (
                <div className="select__search">
                  <Icon name="search" className="select__search-icon" size={15} strokeWidth={1.9} />
                  <input
                    ref={searchRef}
                    type="text"
                    className="select__search-field"
                    value={query}
                    placeholder={t('common.search')}
                    aria-label={t('common.search')}
                    aria-controls={`${panelId}-list`}
                    aria-activedescendant={`${panelId}-${activeIndex}`}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={onKeyDown}
                  />
                </div>
              ) : null}

              {rows.length === 0 ? <p className="select__empty">{t('common.noMatches')}</p> : null}

              <ul
                className="select__list"
                id={`${panelId}-list`}
                role="listbox"
                ref={listRef}
                aria-activedescendant={`${panelId}-${activeIndex}`}
              >
                {rows.map((row, index) => {
                  const isSelected = row.value === value;

                  return (
                    <li
                      key={row.value}
                      id={`${panelId}-${index}`}
                      role="option"
                      aria-selected={isSelected}
                      className={`select__option${index === activeIndex ? ' is-active' : ''}${
                        isSelected ? ' is-selected' : ''
                      }${row.value === '' ? ' select__option--empty' : ''}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => take(index)}
                    >
                      <bdi className="select__option-label">{row.label}</bdi>
                      {isSelected ? (
                        <Icon name="check" className="select__tick" size={15} strokeWidth={2.5} />
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
